import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	OrderObject,
	OrderInfo,
	OrderProduct,
	Shopper,
	UserDetailName,
	UnitSystemType,
	UserProperty,
} from 'common/types';
import { newFirestoreId, getEmptyShopper } from 'common/utils/newRentalUtils';
import {
	getVariantOrderProductsWithUnitsFromOrderProductList,
	getProductNameWithVariant,
} from 'common/utils/productUtils';
import { getTranslation } from 'common/modules/translations';
import {
	detailsIncludeMeasurementUnits,
	getUpdatedShopperFromDetailChange,
	getUpdatedShopperFromUnitSystemChange,
	getSortedRequiredDetails,
} from 'common/utils/userDetails';
import { updateActiveRentalToDb } from 'services/utils';
import { createUpdatedRentalOrderObject } from './utils';
import {
	Theme,
	Grid,
	Typography,
	TextField,
	Dialog,
	Toolbar,
	IconButton,
	Container,
	Paper,
	Button,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { ReactComponent as ArrowBackIcon } from 'common/assets/icons/icon-arrow-back.svg';
import AddProduct from './AddProduct';
import Spinner from 'components/Spinner';
import { useTranslation } from 'services/localization/useTranslation';
import UnitSystemSelect from 'common/components/UnitSystemSelect';
import UserDetailField from 'common/components/UserDetailField';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface OwnProps {
	open: boolean;
	activeRental: OrderObject;
	handleClose: (success: boolean, newShopperId?: string) => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

const AddPerson = (props: Props) => {
	const { classes, open, activeRental, handleClose } = props;

	const language = useSelector(ViewSelectors.lang);
	const shopUnitSystem = useSelector(ShopSelectors.getShopUnitSystem);

	const [newShopper, setNewShopper] = useState<Shopper | null>(null);
	const [newRental, setNewRental] = useState<OrderObject>(activeRental);
	const [productsToAdd, setProductsToAdd] = useState<OrderProduct[]>([]);
	const [updatingOrder, setUpdatingOrder] = useState(false);
	const [addProductsDialogOpen, setAddProductsDialogOpen] = useState(open);
	const [addShopperInfoDialogOpen, setAddShopperInfoDialogOpen] = useState(false);
	const [formErrors, setFormErrors] = useState<{ [field: string]: string }>({});
	const [unitSystem, setUnitSystem] = useState(shopUnitSystem);

	const { rentalInfo, shoppers, products, orderDelivery } = newRental;
	const newShopperId = (newShopper && newShopper.id) || '';
	const { t } = useTranslation();

	useEffect(() => {
		const newShopperId = newFirestoreId();
		const newShopper = getEmptyShopper(newShopperId, rentalInfo.id, rentalInfo.shopId);
		const newShoppers = [...shoppers, newShopper];
		const newRentalInfo: OrderInfo = {
			...rentalInfo,
			shopperIdsWithProducts: {
				...rentalInfo.shopperIdsWithProducts,
				[newShopperId]: [],
			},
		};
		setNewShopper(newShopper);
		setNewRental({
			...newRental,
			shoppers: newShoppers,
			rentalInfo: newRentalInfo,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateNewShopperNames = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		if (newShopper) {
			setNewShopper({
				...newShopper,
				[name]: value,
			});
			if (name === 'firstName') {
				setFormErrors({
					...formErrors,
					[name]: !value ? t('common:mandatory') : '',
				});
			}
		}
	};

	const handleDetailChange = (value: UserProperty | null, detail: UserDetailName) => {
		if (newShopper) {
			const updatedShopper = getUpdatedShopperFromDetailChange(newShopper, detail, value);
			setNewShopper(updatedShopper);
			setFormErrors({ ...formErrors, [detail]: '' });
		}
	};

	const handleUnitSystemChange = (newUnitSystem: UnitSystemType) => {
		setUnitSystem(newUnitSystem);
		newShopper && setNewShopper(getUpdatedShopperFromUnitSystemChange(newShopper, newUnitSystem));
	};

	const handleContinue = (productsToAdd: OrderProduct[]) => {
		setProductsToAdd(productsToAdd);
		setAddProductsDialogOpen(false);
		setAddShopperInfoDialogOpen(true);
	};

	const handleGoBack = () => {
		setAddShopperInfoDialogOpen(false);
		setAddProductsDialogOpen(true);
	};

	const handleAddProductsClose = () => {
		setAddProductsDialogOpen(false);
		handleClose(false);
	};

	const handleSubmit = async () => {
		setUpdatingOrder(true);
		const requiredField = t('common:mandatory');
		let formValid = true;
		const updatedFormErrors: typeof formErrors = {};
		if (!newShopper) {
			return;
		}
		if (!newShopper.firstName) {
			updatedFormErrors.firstName = requiredField;
			formValid = false;
		}
		const details = getSortedRequiredDetails(productsToAdd);
		for (const detail of details) {
			if (!newShopper.userProperties[detail]) {
				updatedFormErrors[detail] = requiredField;
				formValid = false;
			}
		}
		setFormErrors({ ...updatedFormErrors });
		if (formValid) {
			const newShoppers = [...shoppers, newShopper];
			const updatedRentalOrderObject = createUpdatedRentalOrderObject(
				rentalInfo,
				products,
				productsToAdd,
				newShoppers,
				orderDelivery,
			);
			try {
				await updateActiveRentalToDb(updatedRentalOrderObject);
				setProductsToAdd([]);
				setAddProductsDialogOpen(false);
				setAddShopperInfoDialogOpen(false);
				setUpdatingOrder(false);
				handleClose(true, newShopperId);
			} catch (e) {
				setUpdatingOrder(false);
			}
		} else {
			setUpdatingOrder(false);
		}
	};

	const renderShopperInfoDialog = () => {
		const shopperProductsWithUnits = getVariantOrderProductsWithUnitsFromOrderProductList(
			productsToAdd,
		);
		const details = getSortedRequiredDetails(productsToAdd);
		return (
			<Dialog
				fullScreen
				open={addShopperInfoDialogOpen}
				onClose={handleClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<Toolbar className={classes.toolBar}>
					<IconButton
						edge="start"
						color="inherit"
						onClick={handleGoBack}
						aria-label="Back"
						className={classes.icon}
						size="large"
					>
						<ArrowBackIcon />
					</IconButton>
					<Typography variant="h5">
						{t('shopperProducts.addNewPersonInfo', `Add new person's info`)}
					</Typography>
				</Toolbar>
				<Container className={classes.container}>
					<Paper>
						<Grid container className={classes.shopperContainer} data-sl="mask">
							<Grid item xs={12} sm={4}>
								<Typography variant="h6">
									{t('common:form.person')} {shoppers.length}
								</Typography>
								{shopperProductsWithUnits.map((p) => (
									<Typography key={p.id}>
										{p.units}
										{' x '}
										{getProductNameWithVariant(
											getTranslation(p.name, language),
											p.variant || null,
											language,
											undefined,
										)}
									</Typography>
								))}
							</Grid>
							<Grid item xs={12} sm={8} container spacing={1}>
								<Grid item container xs={12} alignItems="baseline" justifyContent="space-between">
									<Grid item xs={12} sm="auto">
										<Typography variant="body1">
											{t('details.customerInformation', 'Customer information')}
										</Typography>
									</Grid>
									{detailsIncludeMeasurementUnits(details) && (
										<Grid item xs={12} sm="auto">
											<UnitSystemSelect
												value={unitSystem}
												onChange={handleUnitSystemChange}
												t={t}
											/>
										</Grid>
									)}
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										id="firstName"
										label={t('common:form.firstName')}
										name="firstName"
										className={classes.textField}
										value={(newShopper && newShopper.firstName) || ''}
										onChange={updateNewShopperNames}
										type="text"
										error={!!formErrors.firstName}
										helperText={formErrors.firstName || ''}
										autoComplete="off"
										variant="outlined"
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										id="lastName"
										label={t('common:form.lastName')}
										name="lastName"
										className={classes.textField}
										value={(newShopper && newShopper.lastName) || ''}
										onChange={updateNewShopperNames}
										type="text"
										error={!!formErrors.lastName}
										helperText={formErrors.lastName || ''}
										autoComplete="off"
										variant="outlined"
									/>
								</Grid>
								{details.map((detail, index) => (
									<Grid item xs={12} sm={6} key={detail + index}>
										<UserDetailField
											detail={detail}
											unitSystem={unitSystem}
											userProperty={newShopper?.userProperties?.[detail]}
											onChange={(newValue) => handleDetailChange(newValue, detail)}
											helperText={formErrors[detail] ?? ''}
											error={!!formErrors[detail]}
											t={t}
										/>
									</Grid>
								))}
							</Grid>
						</Grid>
					</Paper>
					<div className={classes.btnBlockWrapper}>
						<Button
							className={classes.continueBtn}
							variant="contained"
							color="primary"
							disabled={productsToAdd.length === 0 || updatingOrder}
							onClick={handleSubmit}
						>
							{updatingOrder ? (
								<Spinner padding={8} thickness={4} />
							) : (
								t('shopperProducts.updateOrder', 'Update order')
							)}
						</Button>
					</div>
				</Container>
			</Dialog>
		);
	};

	return (
		<>
			{addProductsDialogOpen && (
				<AddProduct
					newPerson={{ handleContinue, productsToAdd }}
					open={addProductsDialogOpen}
					activeRental={newRental}
					activeShopperId={newShopperId}
					handleClose={handleAddProductsClose}
				/>
			)}
			{addShopperInfoDialogOpen && renderShopperInfoDialog()}
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		dialogPaper: {
			backgroundColor: theme.palette.background.default,
		},
		toolBar: {
			display: 'flex',
			justifyContent: 'flex-start',
			alignItems: 'center',
			backgroundColor: theme.palette.background.paper,
		},
		container: {
			paddingTop: theme.spacing(6),
			paddingBottom: theme.spacing(6),
			'-webkit-overflow-scrolling': 'touch',
			height: 'calc(100vh - 56px)', // Height of header bar
			[theme.breakpoints.up('sm')]: {
				height: 'calc(100vh - 64px)', // Height of header bar
			},
		},
		shopperContainer: {
			backgroundColor: theme.palette.background.paper,
			padding: theme.spacing(4),
			margin: theme.spacing(2, 0),
		},
		textField: {
			width: '100%',
		},
		menu: {
			width: 200,
		},
		btnBlockWrapper: {
			display: 'block',
			textAlign: 'center',
			margin: 32,
		},
		continueBtn: {
			minHeight: 40,
		},
		icon: {
			'& svg': {
				height: theme.spacing(2.5),
			},
		},
	});

export default withStyles(styles)(AddPerson);
